"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useTranslations } from "next-intl"
import { useMemo, useState } from "react"
import { FilterIcon, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Apartment, jobFilter } from "utils"
import { ProjectsMessages } from "../Projects"
import AccordionItem from "./Accordion"
import ApartmentDrawerItem from "./ApartmentMarketingForRent/ApartmentDrawerItem"
import DrawerContactForm from "./ApartmentMarketingForRent/DrawerContactForm"
import { useFilters } from "./FilterContext"
import FiltersModal from "./FiltersModal"
import { FormInput } from "./UmbracoApartments/FiltersModal"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid black;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    border: none;
    padding-top: 0;
  }
`

const StyledTop = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${(props) => props.theme.breakpoints.down("md")} {
    padding-top: 32px;
  }
`

const FilterButton = styled(Button)<{ useDrawerLayout?: boolean }>`
  padding: 0;
  min-width: 0;

  svg:hover > rect:first-of-type {
    fill: black;
  }
  svg:hover > rect:nth-of-type(2) {
    stroke: white;
  }
  svg:hover > path {
    fill: white;
  }
`
const ItemsFlex = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
    padding-top: 32px;
  }
`

const StyledMoreButton = styled(Button)`
  text-decoration: none;
  font-size: 18px;
  color: black;
  align-self: center;
  border-bottom: 1px solid #000000;
  width: fit-content;
  border-radius: 0;
  min-width: 0;
  min-height: 0;
  height: fit-content;
  font-weight: 400;
  padding: 0;

  :hover {
    text-decoration: none;
  }
`

const EmptyStateBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px;
  margin-top: 32px;
  border-radius: 16px;
  background-color: #2768a3;
  overflow: hidden;
  position: relative;
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }
  &::before {
    bottom: -40px;
    left: -99px;
  }
  &::after {
    top: -16px;
    right: -75px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 40px;
    align-items: center;
    text-align: center;
  }
`

const EmptyStateCta = styled(Button)`
  border-radius: 200px;
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  height: 48px;
  color: white;
  border: 2px solid white;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: white;
  }
  align-self: flex-end;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-self: center;
  }
`

const StyledFilterCount = styled.div<{ borderRadius?: string }>`
  height: 18px;
  width: 18px;
  border-radius: ${(props) => props.borderRadius ?? "50%"};
  font-size: 14px;
  font-weight: 400;
  color: white;
  background-color: black;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0px;
  right: 10px;
`

interface Character {
  [key: string]: boolean
}

export const createDefaultState = ({
  apartmProp,
  buildProp,
}: {
  apartmProp: string[]
  buildProp: string[]
}): FormInput => {
  const apartmentChar = apartmProp.reduce((acc, prop) => {
    acc[prop] = false
    return acc
  }, {} as Character)
  const buildingChar = buildProp.reduce((acc, prop) => {
    acc[prop] = false
    return acc
  }, {} as Character)
  return {
    floorMax: null,
    floorMin: null,
    areaMax: null,
    areaMin: null,
    date: undefined,
    roomNumber: null,
    apartmentChar,
    buildingChar,
    buildingNames: [],
  }
}

export function filterApartments({
  apartments,
  state,
}: {
  apartments: Apartment[]
  state: FormInput
}) {
  return apartments.filter((ap) => {
    let floorBool = true
    let areaBool = true
    let dateBool = true
    let roomNumberBool = true
    let apartmentCharBool = true
    let buildingCharBool = true
    let buildingNameBool = true

    if (state.floorMin !== null || state.floorMax !== null) {
      const floor = Number(ap.floor)
      const floorMin = state.floorMin ?? -Infinity
      const floorMax = state.floorMax ?? Infinity
      floorBool = ap.floor !== null ? floor >= floorMin && floor <= floorMax : false
    }

    if (state.areaMin !== null || state.areaMax !== null) {
      const area = Number(ap.areaInSquareMeters)
      const areaMin = state.areaMin ?? -Infinity
      const areaMax = state.areaMax ?? Infinity
      areaBool = ap.areaInSquareMeters !== null ? area >= areaMin && area <= areaMax : false
    }

    if (state.date) {
      const formatDate = new Date(state.date).setHours(0, 0, 0)

      dateBool = ap.entryDate ? new Date(ap.entryDate) >= new Date(formatDate) : false
    }
    if (state.roomNumber) {
      roomNumberBool = ap.numberOfRooms === state.roomNumber
    }
    const selectedApChars = Object.entries(state.apartmentChar)
      .filter(([, value]) => value)
      .map((el) => el[0])
    const selectedBuChars = Object.entries(state.buildingChar)
      .filter(([, value]) => value)
      .map((el) => el[0])
    if (selectedApChars.length > 0) {
      apartmentCharBool = selectedApChars.every((el) => ap.apartmentCharacteristics.includes(el))
    }
    if (selectedBuChars.length > 0) {
      buildingCharBool = selectedBuChars.every((el) => ap.buildingCharacteristics.includes(el))
    }
    if (state.buildingNames && state.buildingNames.length > 0) {
      buildingNameBool = ap.buildingTitle ? state.buildingNames.includes(ap.buildingTitle) : false
    }
    return (
      floorBool &&
      areaBool &&
      dateBool &&
      roomNumberBool &&
      apartmentCharBool &&
      buildingCharBool &&
      buildingNameBool
    )
  })
}

type Props = {
  apartments: Apartment[]
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  showHeader?: boolean
  bmbyId?: number
  status?: any
  allApChar: string[]
  allBuChar: string[]
  imageGalleries: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }[]
  companyId?: number
  useDrawerLayout?: boolean
  buildingCharacteristicsText?: string
  bmbyMeetingId?: string | number
  fontFamilyVariable?: string
  phone?: string
  projectTitle?: string
  rentProjectInterestedNum?: number
  rentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
  leaveDetailsButtonText?: string
  noAvailableApartmentsBanner?: {
    title?: string
    text?: string
    button?: { name?: string; url?: string }
  }
  onEmptyStateCta?: () => void
  emailForLead?: string
  leadFormEnabled?: boolean
}

function PropertyTab({
  apartments,
  locale,
  formSubjects,
  messages,
  showHeader = true,
  bmbyId,
  status,
  allApChar,
  allBuChar,
  imageGalleries,
  companyId,
  useDrawerLayout,
  buildingCharacteristicsText,
  bmbyMeetingId,
  fontFamilyVariable,
  phone,
  projectTitle,
  rentProjectInterestedNum,
  rentalProjects,
  cmsProjectLeadIds,
  leaveDetailsButtonText,
  noAvailableApartmentsBanner,
  onEmptyStateCta,
  emailForLead,
  leadFormEnabled,
}: Props) {
  const t = useTranslations("BuildingInfo")
  const { state, setState, defaultState, filtersCount, setFiltersCount, handleReset } = useFilters()

  // Extract unique building names from apartments
  const uniqueBuildings = useMemo(() => {
    const buildingNames = apartments
      .map((ap) => ap.buildingTitle)
      .filter((name): name is string => !!name)
    return Array.from(new Set(buildingNames)).sort()
  }, [apartments])

  // Construct per-building floor ranges from apartments list
  const buildingFloorRanges = useMemo(() => {
    const ranges: Record<string, { min: number; max: number }> = {}
    apartments.forEach((ap) => {
      if (!ap.buildingTitle || ap.floor === null) return
      const floorNum = Number(ap.floor)
      if (!Number.isFinite(floorNum)) return
      if (!ranges[ap.buildingTitle]) {
        ranges[ap.buildingTitle] = { min: floorNum, max: floorNum }
      } else {
        if (floorNum < ranges[ap.buildingTitle].min) ranges[ap.buildingTitle].min = floorNum
        if (floorNum > ranges[ap.buildingTitle].max) ranges[ap.buildingTitle].max = floorNum
      }
    })
    return ranges
  }, [apartments])

  const [open, setOpen] = useState(false)
  const [leadFormOpen, setLeadFormOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(6)

  const handleOpen = () => {
    jobFilter("Filter icon", "Filter icon", "פילטר מתקדם פרויקטים בשיווק ")
    setOpen(true)
  }
  const handleClose = () => setOpen(false)
  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 6)
  }

  // Calculate active filters count from state
  const activeFiltersCount = useMemo(() => {
    let count = 0

    // Count numeric range filters
    if (state.floorMin !== null || state.floorMax !== null) count++
    if (state.areaMin !== null || state.areaMax !== null) count++

    // Count other filters
    if (state.roomNumber) count++
    if (state.date) count++
    if (state.availible) count++

    // Count apartment characteristics
    const activeApChars = Object.values(state.apartmentChar).filter(Boolean).length
    count += activeApChars

    // Count building characteristics
    const activeBuChars = Object.values(state.buildingChar).filter(Boolean).length
    count += activeBuChars

    // Count building names
    if (state.buildingNames && state.buildingNames.length > 0) count++

    return count
  }, [state])

  const allFilteredApartments = filterApartments({ apartments, state })
  const filteredApartments = useDrawerLayout
    ? allFilteredApartments.filter((ap) => ap.moreInfoContentHTML !== "משוריין")
    : allFilteredApartments

  const visibleApartments = filteredApartments.slice(0, visibleCount)

  // An empty board with no filters on means everything is rented, so resetting
  // would change nothing. Rental projects get the same waiting list form the
  // board opens, other projects fall back to their own contact form, and the
  // reset stays for the case where filters are what emptied the list.
  const noFiltersApplied = activeFiltersCount === 0
  const showRentalLeadForm = !!leadFormEnabled && noFiltersApplied
  const showContactCta = !showRentalLeadForm && !!onEmptyStateCta && noFiltersApplied
  const availableCount = useMemo(
    () => filteredApartments.filter((ap) => ap.moreInfoContentHTML !== "משוריין").length,
    [filteredApartments],
  )

  return (
    <StyledContainer>
      <StyledTop>
        {showHeader && (
          <>
            <NewText
              component={"p"}
              desktopFontSize={useDrawerLayout ? 18 : 14}
              desktopLineHeight={useDrawerLayout ? "150%" : "20px"}
              fontWeight={useDrawerLayout ? 300 : undefined}
              mobileFontSize={useDrawerLayout ? 16 : undefined}
              mobileLineHeight={"145%"}
              color={sharedColors.black}
            >
              {t("totalRent")} <strong>{availableCount}</strong> {t("availableApartmentsRent")}
              {state.buildingNames && state.buildingNames.length > 0 && (
                <>
                  {" "}
                  {t("inBuilding")} <strong>{state.buildingNames.join(", ")}</strong>
                </>
              )}
            </NewText>
            {!useDrawerLayout && (
              <FilterButton onClick={handleOpen}>
                {activeFiltersCount > 0 && (
                  <StyledFilterCount>{activeFiltersCount}</StyledFilterCount>
                )}
                <FilterIcon />
              </FilterButton>
            )}
          </>
        )}
        {open ? (
          <FiltersModal
            handleClose={handleClose}
            open={open}
            locale={locale}
            state={state}
            setState={setState}
            setFiltersCount={setFiltersCount}
            filtersCount={filtersCount}
            defaultState={defaultState}
            handleReset={handleReset}
            projectPage={true}
            projectTitle={projectTitle}
            allApChar={allApChar}
            allBuChar={allBuChar}
            availableBuildings={uniqueBuildings}
            buildingFloorRanges={buildingFloorRanges}
          />
        ) : null}
      </StyledTop>

      {visibleApartments.length < 1 && (
        <EmptyStateBanner>
          <NewText
            component={"h3"}
            desktopFontSize={32}
            desktopLineHeight={"36px"}
            mobileFontSize={24}
            mobileLineHeight={"28px"}
            color={"white"}
            sx={{ maxWidth: 675 }}
          >
            {(showRentalLeadForm && noAvailableApartmentsBanner?.title) || t("noApartmentsTitle")}
          </NewText>
          <NewText
            component={"p"}
            desktopFontSize={18}
            desktopLineHeight={"normal"}
            mobileFontSize={16}
            mobileLineHeight={"normal"}
            color={"white"}
            sx={{ maxWidth: 535 }}
          >
            {(showRentalLeadForm && noAvailableApartmentsBanner?.text) || t("noApartmentsText")}
          </NewText>
          {showRentalLeadForm || showContactCta ? (
            <EmptyStateCta
              variant="outlined"
              onClick={showRentalLeadForm ? () => setLeadFormOpen(true) : onEmptyStateCta}
            >
              {noAvailableApartmentsBanner?.button?.name || t("joinWaitingList")}
            </EmptyStateCta>
          ) : (
            <EmptyStateCta variant="outlined" onClick={handleReset}>
              {t("resetFilters")}
            </EmptyStateCta>
          )}
        </EmptyStateBanner>
      )}

      {showRentalLeadForm && (
        <DrawerContactForm
          drawerOpen={leadFormOpen}
          setDrawerOpen={setLeadFormOpen}
          rentalHousingFormProps={{
            onClose: () => setLeadFormOpen(false),
            messages,
            bmbyId,
            companyId,
            emailForLead,
            projects: rentalProjects ?? [],
            cmsProjectLeadIds,
            noAvailableApartements: true,
          }}
        />
      )}

      <ItemsFlex>
        {visibleApartments.map((el, i) => {
          const props = {
            imageGallery: imageGalleries.find((x) => x.numberOfRooms === Number(el.numberOfRooms)),
            locale,
            apartment: el,
            formSubjects,
            messages,
            bmbyId,
            status,
            companyId,
            phone,
          }
          return useDrawerLayout ? (
            <ApartmentDrawerItem
              key={i}
              fontFamilyVariable={fontFamilyVariable}
              buildingCharacteristicsText={buildingCharacteristicsText}
              bmbyMeetingId={bmbyMeetingId}
              rentProjectInterestedNum={rentProjectInterestedNum}
              rentalProjects={rentalProjects}
              cmsProjectLeadIds={cmsProjectLeadIds}
              preSelectedProject={projectTitle}
              leaveDetailsButtonText={leaveDetailsButtonText}
              {...props}
            />
          ) : (
            <AccordionItem key={i} {...props} />
          )
        })}
        {useDrawerLayout && (
          <NewText
            component="div"
            desktopFontSize={18}
            mobileFontSize={16}
            desktopFontWeight="400"
            mobileFontWeight="400"
            desktopLineHeight="145%"
            mobileLineHeight="150%"
            color={"black"}
          >
            {t("imagesForIllustration")}
          </NewText>
        )}
        {visibleCount < filteredApartments.length && (
          <StyledMoreButton variant="text" onClick={handleShowMore}>
            {locale === "he" ? "דירות נוספות" : "More Apartments"}
          </StyledMoreButton>
        )}
      </ItemsFlex>
    </StyledContainer>
  )
}

export default PropertyTab
